import type { WalletAdapter } from "../adapters/WalletAdapter";
import {
  consumeInvite,
  getInviteGate,
  getProfile,
  mintInvites,
  validateInvite,
  proposeName,
  prefetchName,
  recordWallet,
  requestAccess,
} from "../api";
import { readCredential, saveCredential } from "../credentialStore";
import { cachedProfile, rememberProfile } from "../profileCache";
import { WELCOME_KEY, type DeviceFlag } from "../deviceFlag";
import { LOGO_M } from "../logo";
import { recordError } from "../diag";
import { askDisplayName, escapeHtml } from "./dropShared";
import { holdField } from "./holdField";
import { EMOJI_SET, getEmoji } from "../emoji";
import { abortLogin, prepareLogin, prewarmPasskey } from "../passkeyClient";
import { renderInviteView } from "./inviteView";
import { setScreenTheme } from "../theme";
import { arrivalDue, markArrived, prepareArrival, showArrival } from "../splash";
import { playDone } from "../sound";

/** v0.90.14 — which mark is the login button: the M or the identity emoji.
 *  v1.1.4 (30 Sep) — the EMOJI with the breathing halo is the default again:
 *  the wallet is the person's own space (branding.md, "Two faces, one brand");
 *  the M stays the mark of the rail. `m.loginMark = "logo"` restores the M.
 *  Device-local; not synced — it's a presentation choice. */
function loginMark(): "logo" | "emoji" {
  try {
    return localStorage.getItem("m.loginMark") === "logo" ? "logo" : "emoji";
  } catch {
    return "emoji";
  }
}

/** v1.1.6 — the accessible name of the emoji button: the person's own name
 *  (the one they typed at creation), never the wallet code. */
function loginLabel(name: string | null | undefined): string {
  const n = (name ?? "").replace(/\s+/g, " ").trim();
  return n ? `Log in as ${n}` : "Log in with Face ID";
}

/** v1.1.6 — the emoji the wallet's profile says, if it's newer than what this
 *  browser has. The synced settings carry it with a stamp (settings.ts);
 *  the profile's own `emoji` column is the fallback when nothing is stamped
 *  here. Returns null when this browser is already right (or can't tell). */
function remoteEmoji(p: { emoji: string | null; settings?: string | null }): string | null {
  const ok = (e: unknown): e is string => typeof e === "string" && EMOJI_SET.includes(e);
  let localAt = 0;
  let localPick: string | null = null;
  try {
    localPick = localStorage.getItem("m.emoji");
    const bag = JSON.parse(localStorage.getItem("m.settings") ?? "{}") as Record<string, { at?: number }>;
    localAt = bag.emoji?.at ?? 0;
  } catch {
    /* fall through */
  }
  try {
    const remote = p.settings ? (JSON.parse(p.settings) as Record<string, { v?: unknown; at?: number }>).emoji : undefined;
    if (remote && ok(remote.v) && (remote.at ?? 0) > localAt) return remote.v;
  } catch {
    /* malformed settings — ignore */
  }
  if (!localPick && ok(p.emoji)) return p.emoji;
  return null;
}

let visBound = false; // one visibility listener for the login prep, however often the screen paints
// v1.1.8 — keep the prepared challenge fresh while the login screen is open.
// prepareLogin() is a no-op while the one it holds is under 45 s old, so this
// costs one Circle request per ~45 s on this screen and nothing elsewhere.
let freshTimer: number | undefined;
function keepFresh(): void {
  if (freshTimer !== undefined) return;
  freshTimer = window.setInterval(() => {
    if (!document.querySelector(".welcome.login-focus, .land69")) { window.clearInterval(freshTimer); freshTimer = undefined; return; }
    if (document.visibilityState === "visible") prepareLogin();
  }, 20_000);
}

// Create / login ceremony (spec §9). v0.11 #2 hard guard: if this browser
// already has an M wallet (remembered flag), the welcome screen shows ONLY
// "Log in" — no create path — so a normal user can't make an accidental second
// wallet. A safety valve appears only if that login actually fails.
//
// v0.20: creation is gated behind an invite code when the runtime gate is on.
// The gate sits ONLY on the create branch — a returning user (remembered) logs
// in and never sees the code wall. A deep link (mdrop.it/invite/CODE) arrives
// as invitePrefill and, if the gate is on and this browser has no wallet, opens
// straight on the pre-filled invite screen.

interface CreateViewDeps {
  adapter: WalletAdapter;
  remembered: DeviceFlag;
  onReady: (address: string, desiredSlug?: string, givenName?: string | null) => void;
  onError: (message: string) => void;
  /** Invite code from a deep link (create path only). */
  invitePrefill?: string;
  /**
   * v0.60.1 (S10) — standalone (Home-Screen) launch. An iOS Home-Screen web app
   * has its OWN storage partition, so none of the m.* device flags carry over
   * from Safari — but passkeys are per-origin and DO. So with no flag we still
   * lead with Log in (Create only as the repeated-failure fallback), or users
   * would mint a second wallet from the icon.
   */
  loginFirst?: boolean;
  /** 1.4.37 — arrived at /join (betterthan.cash's "Got a code?"): open on the code screen. */
  openCode?: boolean;
  /** 1.4.83 — arrived at /login (betterthan.cash's Login): the landing, and the open login at once. */
  openLogin?: boolean;
}

export function renderCreateView(
  root: HTMLElement,
  { adapter, remembered, onReady, onError, invitePrefill, loginFirst, openCode, openLogin }: CreateViewDeps,
) {
  // Count consecutive login failures so a single cancelled Face ID doesn't push
  // the "new wallet" escape hatch at a user who simply dismissed the prompt.
  let loginFails = 0;

  // v1.1.6 — the login contract. This browser is BOUND to a wallet when it
  // remembers one (flag + name) AND holds that wallet's credential: then the
  // emoji shows, and the login is pinned to that one passkey, so what you see
  // is what opens. Anything less (first visit, cleared storage, just switched
  // wallets) shows the M and the open login: no prediction to get wrong.
  const cred = readCredential();
  const bound = !!(remembered.has && remembered.name && cred?.id && cred.address);

  // 1.4.39 — first time here (no wallet in this browser, not the installed app): Pure, as
  // betterthan.cash; the login of a remembered wallet: the person's theme (Dusk by default).
  const firstTime = !remembered.has && !loginFirst;
  function paintWelcome(ask = false) {
    setScreenTheme(firstTime ? "light" : null);
    prewarmPasskey(); // warm DNS/TLS to Circle so Face ID fires sooner
    {
      // v1.1.5 — and fetch the login challenge now, so the tap goes straight
      // to Face ID (the Send card's "prepare before the slide", for login).
      // 1.4.83 — the landing too: its Login opens the phone's sheet on the tap.
      prepareLogin();
      keepFresh();
      if (!visBound) {
        visBound = true;
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible" && document.querySelector(".welcome.login-focus, .land69")) prepareLogin();
        });
      }
    }
    root.innerHTML = !remembered.has && loginFirst
      ? `
      <main class="screen welcome login-focus">
        <div class="welcome-head">
          <div class="brand">${LOGO_M}</div>
          <h1 class="serif21">Welcome back.</h1>
          <p class="sub">Log in with Face ID to open your wallet.</p>
        </div>
        <div class="actions">
          <button id="login" class="btn primary">Log in</button>
        </div>
        <p id="status" class="status" role="status" aria-live="polite"></p>
      </main>
    `
      : remembered.has
      ? `
      <main class="screen welcome login-focus${bound && loginMark() !== "logo" ? " login-centre" : ""}">
        <div class="welcome-head">
          ${bound && loginMark() === "logo" ? "" : `<div class="brand">${LOGO_M}</div>`}
          <h1 class="serif21">Welcome back.</h1>
        </div>
        <div class="actions">
          ${
            bound
              ? loginMark() === "logo"
                // v0.90.14 — the M is the login button: one mark, naked, lit
                // along its own edge. The emoji stays everyone's avatar
                // elsewhere; here, alone and unnamed, it invited a "why?".
                // `m.loginMark = "emoji"` restores the emoji button.
                ? `<button id="login" class="login-emoji login-logo" type="button" aria-label="Log in with Face ID"><span class="le-emoji">${LOGO_M}</span></button>`
                : `<button id="login" class="login-emoji" type="button" aria-label="${escapeHtml(loginLabel(cachedProfile(cred!.address!)?.name))}"><span class="le-emoji">${getEmoji(remembered.name!)}</span></button>`
              : `<button id="login" class="btn primary">Log in</button>`
          }
        </div>
        <p id="status" class="status" role="status" aria-live="polite"></p>
        ${bound ? `<button id="loginOther" class="linkish small muted login-other" type="button" hidden>Use another wallet</button>` : ""}
      </main>
    `
      : `
      <main class="screen welcome lg21 land69 slot69-screen">
        <!-- 1.4.69 (Alexander, 21 Oct) — three blocks, as betterthan.cash: the M and the claim as before;
             "Got a code?" a short crimson glass pill in the middle (red: the new world; the one place the
             action isn't green), Request access muted under it with room to breathe; the lockup at the foot. No Login here: a
             returning wallet is recognised, and an existing one in a new browser logs in from the code screen. -->
        <div class="brand">${LOGO_M}</div>
        <h1 class="serif21">Better than cash.</h1>
        <!-- 1.4.70 — one slot, the pill's: the code and name fields stand in this same place on their screens. -->
        <div class="land69-mid"></div>
          <div class="slot69">
            <button id="create" class="pill69" type="button">Got a code?</button>
            <form id="raForm" class="land69-form" novalidate hidden>
              <input id="raEmail" class="nick-input land69-email" type="email" inputmode="email" autocomplete="email"
                     autocapitalize="off" autocorrect="off" spellcheck="false" placeholder="Email" maxlength="254"
                     aria-label="Email" />
              <button id="raGo" class="pill69" type="submit" disabled>Request access</button>
            </form>
            <p id="raThanks" class="serif21 land69-thanks" hidden>Thanks — we'll be in touch.</p>
            <div class="below69">
              <p id="status" class="status land69-status" role="status" aria-live="polite"></p>
              <button id="ask" class="linkish muted login21 land69-ask" type="button">Request access</button>
            </div>
          </div>
        <footer class="land69-foot">
          <!-- 1.4.83 (Alexander, 22 Oct) — a returning person in a browser with no record of M: Login, muted, over the
               lockup. The phone's sheet lists every M® passkey here; none (or a cancel) → Request access. -->
          <button id="landLogin" class="linkish muted land69-login" type="button">Login</button>
          <p class="land69-lock">M® · The Global Money App</p>
        </footer>
      </main>
    `;

    const createBtn = root.querySelector<HTMLButtonElement>("#create"); // absent in welcome-back
    if (createBtn) createBtn.addEventListener("click", () => void startCreate());
    if (createBtn) {
      wireLandingAsk(ask);
      // 1.4.83 — the open ceremony, as "Use another wallet": the sheet lists every M® passkey on the device.
      root.querySelector<HTMLButtonElement>("#landLogin")?.addEventListener("click", () => void run("login", undefined, true, undefined, "landing"));
      return;
    }
    const loginBtn = root.querySelector<HTMLButtonElement>("#login")!;
    loginBtn.addEventListener("click", () => void run("login"));
    // v1.1.6 — the open ceremony: Apple's sheet lists every M® passkey on the
    // device, each labelled "M® · {name}" by its owner at creation.
    root.querySelector<HTMLButtonElement>("#loginOther")?.addEventListener("click", () => void run("login", undefined, true));
    if (bound) refreshMark(loginBtn);

    // Tactile press feedback for the emoji login control. Pointer-driven so it's
    // instant on iOS; it only toggles a class and never gates the click that
    // fires the passkey ceremony.
    const press = () => loginBtn.classList.add("is-press");
    const release = () => loginBtn.classList.remove("is-press");
    loginBtn.addEventListener("pointerdown", press);
    loginBtn.addEventListener("pointerup", release);
    loginBtn.addEventListener("pointercancel", release);
    loginBtn.addEventListener("pointerleave", release);
    if (loginBtn.classList.contains("login-emoji")) {
      loginBtn.classList.add("is-enter");
      setTimeout(() => loginBtn.classList.remove("is-enter"), 700);
    }
  }

  // 1.4.69 — Request access on the landing opens in the pill's place: the email field and a crimson
  // pill; the link under it reads "I have a code" to go back. One answer, in the same place.
  function wireLandingAsk(openAsk = false) {
    const mid = root.querySelector<HTMLElement>(".land69-mid")!;
    const pill = root.querySelector<HTMLButtonElement>("#create")!;
    const form = root.querySelector<HTMLFormElement>("#raForm")!;
    const email = root.querySelector<HTMLInputElement>("#raEmail")!;
    const go = root.querySelector<HTMLButtonElement>("#raGo")!;
    const thanks = root.querySelector<HTMLElement>("#raThanks")!;
    const toggle = root.querySelector<HTMLButtonElement>("#ask")!;
    const statusEl = root.querySelector<HTMLElement>("#status")!;
    holdField(email, () => root.querySelector("main h1")); // 1.4.70 — the fixed-position rule
    let asking = false;
    let sent = false;
    const say = (t: string, warn = false) => { statusEl.textContent = t; statusEl.classList.toggle("warn", warn); };
    const show = (on: boolean, focus = true) => {
      asking = on;
      pill.hidden = on;
      form.hidden = !on;
      mid.classList.toggle("asking", on);
      toggle.textContent = on ? "I have a code" : "Request access";
      say("");
      if (on && focus) setTimeout(() => email.focus({ preventScroll: true }), 0);
    };
    // 1.4.83 — after a Login that found no wallet: Request access, open, the field waiting for a tap.
    if (openAsk) show(true, false);
    toggle.addEventListener("click", () => { if (!sent) show(!asking); });
    const looksLikeEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@.]{2,}$/.test(v.trim());
    email.addEventListener("input", () => { go.disabled = !looksLikeEmail(email.value); say(""); });
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (go.disabled || sent) return;
      go.disabled = true;
      email.readOnly = true;
      say("Sending…");
      const r = await requestAccess(email.value.trim());
      if (r === "ok") {
        sent = true;
        email.blur();
        form.hidden = true;
        toggle.hidden = true;
        thanks.hidden = false;
        say("");
        return;
      }
      email.readOnly = false;
      go.disabled = false;
      say(
        r === "invalid" ? "That email doesn't look right. Check it and try again."
          : r === "busy" ? "Too many requests from here. Try again in an hour."
          : "Couldn't send that. Try again in a moment.",
        true,
      );
    });
  }

  // v1.1.6 — the cached emoji paints at once; the wallet's public profile is
  // read alongside the challenge prep, and if the emoji was changed on another
  // device it cross-fades in before the tap. Never blocks, never spins. The
  // person's own name becomes the button's accessible label.
  function refreshMark(btn: HTMLButtonElement) {
    const address = cred?.address;
    if (!address) return;
    getProfile(address)
      .then((p) => {
        rememberProfile(address, p);
        if (!btn.isConnected) return;
        btn.setAttribute("aria-label", loginLabel(p.name));
        const next = remoteEmoji(p);
        const span = btn.querySelector<HTMLElement>(".le-emoji");
        if (!next || !span || span.textContent === next || btn.disabled) return;
        span.style.transition = "opacity 0.18s ease";
        span.style.opacity = "0";
        setTimeout(() => {
          span.textContent = next;
          span.style.opacity = "1";
        }, 180);
      })
      .catch(() => { /* offline: the cached emoji stands */ });
  }

  // Gate-aware entry to the create path (from the "Create wallet" button).
  async function startCreate() {
    const createBtn = root.querySelector<HTMLButtonElement>("#create");
    if (createBtn) createBtn.disabled = true;
    const gate = await readGate();
    if (createBtn) createBtn.disabled = false;
    if (gate === "on") paintInvite();
    else void run("register"); // gate off → today's behaviour, straight to Face ID
  }

  // From a deep link: if the gate is on, open the pre-filled invite screen; if
  // it's off, the code is moot, so just show the normal welcome (no auto-Face-ID).
  // v1.1.26 — an invite is a link: whoever arrives with one never sees the
  // code page. The code is checked quietly; good → straight to the name and
  // Face ID; used or unknown → the code page, saying why.
  async function maybeInviteFromLink(prefill: string) {
    const gate = await readGate();
    if (gate !== "on") { paintWelcome(); return; }
    // 1.4.37 (Alexander, 17 Oct) — the code screen itself, filled in and checked at once:
    // "If you have a code, you already know." for a moment, then "Access granted", then the
    // name. A used or unknown code stays on the screen with the reason.
    paintInvite(prefill);
  }

  // Gate read is fail-open: if the worker can't be reached we fall back to "off"
  // (creation open), matching pre-v0.20 behaviour rather than stranding users on
  // an outage. Flip to fail-closed here if the gate must hold during downtime.
  async function readGate(): Promise<"on" | "off"> {
    try {
      return (await getInviteGate()).gate === "on" ? "on" : "off";
    } catch {
      return "off";
    }
  }

  function paintInvite(prefill?: string, initialError?: string, ask = false) {
    setScreenTheme("light"); // 1.4.39 — the code screen is Pure
    renderInviteView(root, {
      prefillCode: prefill,
      initialError,
      ask,
      onContinue: (code) => void run("register", code),
      onLogin: () => void run("login"), // 1.4.69 — the landing has no Login; the code screen's goes straight to Face ID
    });
  }

  async function run(mode: "register" | "login", inviteCode?: string, open = false, again?: { name: string; note: string }, from?: "landing" | "link") {
    // v1.1.6 — pinned to this browser's wallet unless asked to open the sheet.
    const pin = mode === "login" && bound && !open ? cred!.id : undefined;
    if (mode === "login") prepareArrival(); // 1.5.10 — the day's first login: the arrival's sound is fetched while Face ID runs
    // v0.70.12 — "What shall we call you?" before the passkey ceremony, so the
    // passkey list reads "M® · Name". Skippable; saved as the profile name.
    let displayName: string | null = null;
    // v1.1.26 — a create never starts while a login is still outstanding (WebKit
    // refuses the second ceremony), and the wallet code is fetched while the
    // name is typed, so less network sits between the tap and Face ID.
    let proposedP: ReturnType<typeof proposeName> | null = null;
    if (mode === "register") {
      abortLogin();
      proposedP = proposeName();
      proposedP.catch(() => {});
      displayName = await askDisplayName(root, { slot: true, value: again?.name, note: again?.note }); // 1.4.70: the take page's box on the slot — → brings Face ID
      // 1.4.72 (Alexander's video, 21 Oct) — the name screen stays, its box "Creating your wallet…" as the take page's,
      // until the home paints: the landing is never painted again behind Face ID.
      const act = root.querySelector<HTMLElement>("#askAct");
      if (act) { root.querySelector<HTMLInputElement>("#askName")?.blur(); act.dataset.mode = "making"; } else paintWelcome();
    }
    const statusEl = root.querySelector<HTMLElement>("#status");
    const loginBtn = root.querySelector<HTMLButtonElement>("#login");
    const createBtn = root.querySelector<HTMLButtonElement>("#create");
    const contBtn = root.querySelector<HTMLButtonElement>("#cont"); // invite screen
    const otherBtn = root.querySelector<HTMLButtonElement>("#loginOther");
    const landLogin = root.querySelector<HTMLButtonElement>("#landLogin");
    const setBusy = (busy: boolean, msg = "") => {
      if (landLogin) landLogin.disabled = busy;
      if (loginBtn) loginBtn.disabled = busy;
      if (otherBtn) otherBtn.disabled = busy;
      if (createBtn) createBtn.disabled = busy;
      if (contBtn) contBtn.disabled = busy;
      if (statusEl) statusEl.textContent = msg;
    };

    setBusy(true, "◉ Waiting for Face ID…");
    try {
      // New wallet: register the passkey UNDER a proposed wallet code so the OS
      // Circle username = the code (unique). The passkey-list label is set
      // separately in passkeyClient (v0.60.14 S11): "M® Wallet · XXXX-XXXX".
      let label: string | undefined;
      let desiredSlug: string | undefined;
      if (mode === "register") {
        // v1.1.26 — the code once more, just before Face ID: a code taken while
        // the name was typed is caught here, before any wallet exists.
        if (inviteCode) {
          const v = await validateInvite(inviteCode).catch(() => ({ valid: true } as { valid: boolean; reason?: string }));
          if (!v.valid) {
            paintInvite(inviteCode, v.reason === "used" ? "That code was used a moment ago. Ask your inviter for another." : "That code isn't valid. Check it and try again.");
            return;
          }
        }
        try {
          const proposed = await (proposedP ?? proposeName());
          label = proposed.slug;
          desiredSlug = proposed.slug;
        } catch {
          label = `m-wallet-${Date.now().toString(36)}`;
        }
      }
      const { address, credential } = await adapter.createOrConnect(mode, label, displayName, pin);
      // v1.1.28 — the name goes on with onReady: main.ts saves it once the
      // wallet's name row exists (here it doesn't yet). One name: the passkey's and M's.
      // Cache the credential so future payments need a single Face ID. v1.1.6 —
      // this also (re)binds the browser: a different wallet than the one bound
      // here puts the old one's state aside (walletSwitch.ts).
      const switched = saveCredential({ ...credential, address });
      // 1.5.10 — the day's first login arrives with M's splash, over the home as it renders beneath (the hand-through
      // below stands aside for it); a new wallet is a completion: M's "done", and today's arrival counts as had.
      const arriving = mode === "login" && arrivalDue();
      if (arriving) showArrival(() => !!document.querySelector("#app .dock-inner"));
      else if (mode === "register") { markArrived(); playDone(); }
      // Hand-through: on an emoji login the circle pumps then blossoms outward
      // while the screen fades, carrying you into the wallet. It runs during the
      // recordWallet round-trip below (so it fills that time, not adds latency).
      // Reduced-motion and non-emoji paths skip it; onReady is always reached.
      const welcome = root.querySelector<HTMLElement>(".welcome");
      const reduceMotion = !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      let handThrough: Promise<unknown> | null = null;
      // A switched wallet skips it: the emoji on screen was the OTHER wallet's.
      if (!arriving && !switched && !reduceMotion && welcome && loginBtn?.classList.contains("login-emoji")) {
        welcome.classList.add("is-exiting");
        handThrough = new Promise((res) => setTimeout(res, 500));
      }
      // 1.4.75 (Alexander's video, 21 Oct: ~2.5 s on "Creating your wallet…" after Face ID) — the Worker's calls
      // no longer run one after another. The home needs the name and, for an invite, the welcome amount; those two
      // run together with the wallet's record (none needs another: /invite/consume and /name/assign stand alone),
      // and the rest — the wallet's own invite codes, the profile — runs behind the home (main.ts toHome).
      if (mode === "register") prefetchName(address, desiredSlug);
      const recorded = recordWallet(address, label ?? "returning").catch((e) => {
        console.warn("[M] wallet/record failed (non-blocking):", e);
      });
      // v0.20: consume the invite + mint this wallet's own codes. Both are
      // non-blocking — the passkey wallet already exists on-chain regardless, so
      // a lost consume race or a transient error must not strand the user. Mint
      // is idempotent and also re-run when the "Your invites" sheet opens.
      let inviteLost = false;
      if (inviteCode) {
        try {
          const [claim] = await Promise.all([consumeInvite(inviteCode, address), recorded]);
          if (!claim.ok) {
            console.warn("[M] invite consume not applied (race/used/error):", claim.error);
            inviteLost = /code_unavailable/.test(claim.error ?? ""); // 409: the code went to another wallet
          } else {
            // v1.3.0 — the invite is used, so the Worker is funding this wallet now:
            // the home welcomes the money (dropCatchView, welcomeMoney).
            // v1.3.1 — with the amount the Worker says it is sending (1.3.2's `welcome`),
            // when it says one: the home can then say the line while the money is on its way.
            try { localStorage.setItem(WELCOME_KEY, JSON.stringify({ a: address, at: Date.now(), ...(claim.welcome ? { amt: claim.welcome } : {}) })); } catch { /* no welcome line; nothing else changes */ }
          }
          void mintInvites(address).catch((e) => console.warn("[M] invite mint failed (non-blocking):", e)); // 1.4.75 — behind the home (idempotent; the invites sheet mints again)
        } catch (e) {
          console.warn("[M] invite consume failed (non-blocking):", e);
        }
      } else {
        await recorded;
      }
      if (handThrough) await handThrough;
      // v1.1.26 — the wallet exists either way; when the invite was taken in
      // the seconds of Face ID, say so plainly rather than carry on in silence.
      if (inviteLost) {
        root.innerHTML = `
          <main class="screen welcome invite lg21">
            <div class="brand">${LOGO_M}</div>
            <h1 class="serif21">Your wallet is ready.</h1>
            <p class="sub">The invite had already been used by someone else, so no welcome money came with it. You can add money yourself at any time.</p>
            <div class="actions"><button id="ilGo" class="btn primary block">Open my wallet</button></div>
          </main>`;
        await new Promise<void>((res) => root.querySelector("#ilGo")!.addEventListener("click", () => res(), { once: true }));
      }
      onReady(address, desiredSlug, displayName);
    } catch (e) {
      console.error(e);
      recordError(`create/${mode}`, e); // 1.4.31 — also feeds the in-app test strip
      setBusy(false);
      const err = e as {
        name?: string;
        message?: string;
        shortMessage?: string;
        details?: string;
      };
      const detail =
        err.details || err.shortMessage || err.message || err.name || String(e);

      if (from && mode === "login") {
        // 1.4.83 — the landing's Login. A page can't tell "no M® passkey here" from a cancel (both are
        // NotAllowedError), so either way the person meets Request access; Login stays under it to try again.
        // From betterthan.cash's Login (/login), the first try runs without a tap and a browser may refuse it
        // unseen — that one leaves the landing as it was.
        prepareLogin();
        paintWelcome(from === "landing");
      } else if ((remembered.has || loginFirst) && mode === "login") {
        loginFails += 1;
        // v1.1.8 — a fresh challenge for the next tap, at once. The one just
        // used is spent; without this the next tap had to fetch first, iOS no
        // longer counted it as the tap, and refused Face ID with the same
        // error as a cancel ("Use another wallet" → "Cancelled").
        prepareLogin();
        // A cancelled/timed-out Face ID reports NotAllowedError — the SAME error
        // as a genuinely missing passkey. So we lead with retry, and only after
        // it keeps failing do we surface the "set up a new wallet" safety valve.
        const cancelled = err.name === "NotAllowedError";
        if (loginBtn) {
          loginBtn.classList.add("is-fail");
          setTimeout(() => loginBtn.classList.remove("is-fail"), 360);
        }
        if (statusEl) {
          statusEl.textContent = cancelled
            ? "Cancelled — tap to try again."
            : "Couldn't log in — tap to try again.";
        }
        // v1.1.6 — the ladder. Pinned: on the second failure this wallet's
        // passkey may simply not be on this device, so "Use another wallet"
        // steps forward; "Set up a new wallet" only on the third. Open: as before.
        // v1.1.8 — the link appears after the first cancelled or failed Face
        // ID: most people never need it; whoever wants another wallet on
        // purpose finds it with one cancel.
        if (otherBtn) otherBtn.hidden = false;
        if (pin && loginFails >= 2 && otherBtn) {
          otherBtn.className = "btn ghost small login-other promoted";
          if (statusEl) statusEl.textContent = "Can't open this wallet? Its passkey may not be on this device.";
        }

        if (loginFails >= (bound ? 3 : 2) && !root.querySelector("#createFallback")) {
          const wrap = document.createElement("div");
          wrap.id = "createFallback";
          wrap.style.marginTop = "26px"; // separate from the retry line above
          wrap.style.textAlign = "center";
          const note = document.createElement("p");
          note.className = "sub small";
          note.style.margin = "0 auto";
          note.textContent = bound
            ? "Or start again: a new wallet starts fresh — it won't recover this one."
            : "Still can't log in? Your passkey may have been removed from this device. A new wallet starts fresh — it won't recover this one.";
          const b = document.createElement("button");
          b.className = "btn ghost small";
          b.style.marginTop = "18px"; // breathing room before the button
          b.textContent = "Set up a new wallet";
          b.addEventListener("click", () => {
            wrap.remove();
            // v1.1.26 — through the invite like Join M: with the gate on, every
            // new wallet uses an invite (and gets its welcome money).
            abortLogin();
            void startCreate();
          });
          wrap.append(note, b);
          (otherBtn ?? statusEl)?.after(wrap);
        }
      } else if (mode === "register" && inviteCode) {
        // Keep the validated code and let them retry a cancelled/failed Face ID
        // rather than bouncing back to welcome. 1.4.39 — the code screen has no button since
        // 1.4.37, so the retry is the name screen again, the name kept: Create wallet = Face ID.
        void run("register", inviteCode, false, {
          name: displayName ?? "",
          note: err.name === "NotAllowedError"
            ? "Face ID didn't complete. Tap → to try again."
            : `Couldn't create your wallet — ${detail}`,
        });
      } else {
        onError(`${mode === "register" ? "Create" : "Login"} failed — ${detail}`);
      }
    }
  }

  // Boot: a deep-link invite (and no wallet here yet) opens the invite flow;
  // otherwise the normal welcome / welcome-back screen.
  if (invitePrefill && !remembered.has) {
    void maybeInviteFromLink(invitePrefill);
  } else if (openLogin && !remembered.has && !loginFirst) {
    paintWelcome();
    void run("login", undefined, true, undefined, "link"); // 1.4.83 — /login: betterthan.cash's Login
  } else if (openCode && !remembered.has) {
    void startCreate(); // 1.4.37 — /join (betterthan.cash's "Got a code?"): straight to the code screen
  } else {
    paintWelcome();
  }
}
